import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../../api.ts';
import { INCIDENT_LABELS, fmtMinutes } from '../../ratings.ts';
import { formatEuros } from '../../format.ts';

interface Alert {
  id: string;
  kind: 'LOW_RATING' | 'CONSECUTIVE_LOW' | 'INCIDENT_REPORTED';
  createdAt: string;
  extraName: string | null;
  organizationName: string | null;
  incidentId: string | null;
  average: number | null;
  lowReason: string | null;
  comment: string | null;
}

interface Incident {
  id: string;
  kind: 'NO_SHOW' | 'EARLY_DEPARTURE';
  note: string | null;
  actualEnd: string | null;
  extraResponse: string | null;
  extraRespondedAt: string | null;
  createdAt: string;
  missionTitle: string;
  missionStartsAt: string;
  missionEndsAt: string;
  extraName: string;
  organizationName: string;
}

interface Dispute {
  id: string;
  kind: 'MORE' | 'LESS';
  plannedMinutes: number;
  actualMinutes: number;
  orgNote: string | null;
  extraNote: string | null;
  createdAt: string;
  missionTitle: string;
  missionStartsAt: string;
  hourlyRateCents: number;
  extraName: string;
  organizationName: string;
}

const KIND = { LOW_RATING: 'Avis bas', CONSECUTIVE_LOW: 'Série d’avis bas', INCIDENT_REPORTED: 'Incident signalé' } as const;
const day = (iso: string) => new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

export function AdminModeration() {
  const [data, setData] = useState<{ alerts: Alert[]; incidents: Incident[]; disputes: Dispute[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<{ alerts: Alert[]; incidents: Incident[]; disputes: Dispute[] }>('/admin/moderation').then(setData);
  }, []);
  useEffect(load, [load]);

  async function act(path: string, prompt: string, body: (note: string) => object) {
    const note = window.prompt(prompt);
    if (!note) return;
    setError(null);
    try {
      await api.post(path, body(note));
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'Action impossible.');
    }
  }

  if (!data) return <div className="empty-state">Chargement…</div>;
  const alerts = data.alerts.filter((a) => a.kind === 'LOW_RATING' || a.kind === 'CONSECUTIVE_LOW');

  function decide(d: Dispute) {
    const raw = window.prompt(`Minutes à payer (0 à ${Math.max(d.plannedMinutes, d.actualMinutes)}). Prévu : ${d.plannedMinutes} min, pointé : ${d.actualMinutes} min.`, String(d.plannedMinutes));
    if (raw === null) return;
    const minutes = Number(raw);
    act(`/admin/moderation/disputes/${d.id}/decide`, 'Motif de la décision (visible par les deux parties) :', (note) => ({ billableMinutes: minutes, note }));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {error && <div className="error-banner">{error}</div>}
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        Aucune sanction n'est automatique : les signalements et les avis bas arrivent ici, l'extra peut répondre, et une personne décide. Pour suspendre un compte, utilisez la page « Utilisateurs ».
      </div>

      <section>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>Médiations d'heures ({data.disputes.length})</div>
        {data.disputes.length === 0 && <div className="empty-state">Aucune médiation en attente.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.disputes.map((d) => (
            <div key={d.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 800 }}>{d.kind === 'MORE' ? 'Plus d’heures que prévu' : 'Moins d’heures que prévu'} · {d.extraName}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>ouvert le {day(d.createdAt)}</div>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{d.missionTitle} chez {d.organizationName}, le {day(d.missionStartsAt)}</div>
              <div style={{ fontSize: 13.5 }}>
                Prévu <strong>{fmtMinutes(d.plannedMinutes)}</strong> ({formatEuros(Math.round((d.hourlyRateCents * d.plannedMinutes) / 60))}) · Pointé <strong>{fmtMinutes(d.actualMinutes)}</strong> ({formatEuros(Math.round((d.hourlyRateCents * d.actualMinutes) / 60))})
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.5 }}><strong>Établissement :</strong> {d.orgNote ?? <span style={{ color: 'var(--text-tertiary)' }}>pas de version</span>}</div>
              <div style={{ fontSize: 13, lineHeight: 1.5 }}><strong>Extra :</strong> {d.extraNote ?? <span style={{ color: 'var(--text-tertiary)' }}>pas de version</span>}</div>
              <button className="btn btn-primary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => decide(d)}>Trancher</button>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>Incidents à instruire ({data.incidents.length})</div>
        {data.incidents.length === 0 && <div className="empty-state">Aucun incident en attente.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.incidents.map((i) => (
            <div key={i.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 800 }}>{INCIDENT_LABELS[i.kind]} · {i.extraName}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>signalé le {day(i.createdAt)}</div>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {i.missionTitle} chez {i.organizationName}, du {day(i.missionStartsAt)} au {day(i.missionEndsAt)}
                {i.actualEnd && <> · départ réel à {day(i.actualEnd)}</>}
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.5 }}><strong>Établissement :</strong> {i.note ?? 'aucun commentaire'}</div>
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                <strong>Version de l'extra :</strong> {i.extraResponse ? <>{i.extraResponse} <span style={{ color: 'var(--text-tertiary)' }}>({day(i.extraRespondedAt!)})</span></> : <span style={{ color: 'var(--text-tertiary)' }}>pas encore de réponse</span>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-danger-outline btn-sm" onClick={() => act(`/admin/moderation/incidents/${i.id}/confirm`, 'Motif de la confirmation (visible par l’extra) :', (note) => ({ note }))}>Confirmer</button>
                <button className="btn btn-secondary btn-sm" onClick={() => act(`/admin/moderation/incidents/${i.id}/dismiss`, 'Motif du classement sans suite (visible par l’extra) :', (note) => ({ note }))}>Classer sans suite</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>Avis à lire ({alerts.length})</div>
        {alerts.length === 0 && <div className="empty-state">Aucun avis en attente.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {alerts.map((a) => (
            <div key={a.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                <div><span className="badge badge-pending">{KIND[a.kind]}</span> <strong>{a.extraName ?? a.organizationName}</strong></div>
                <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{day(a.createdAt)}</div>
              </div>
              {a.average !== null && <div style={{ fontSize: 13 }}>Note moyenne : <strong>{a.average.toFixed(2)}/5</strong></div>}
              {a.lowReason && <div style={{ fontSize: 13, lineHeight: 1.5 }}><strong>Raison du mécontentement :</strong> {a.lowReason}</div>}
              {a.comment && <div style={{ fontSize: 13, lineHeight: 1.5 }}><strong>Commentaire :</strong> « {a.comment} »</div>}
              <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => act(`/admin/moderation/alerts/${a.id}/resolve`, 'Note de traitement :', (note) => ({ note }))}>Marquer comme traité</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
